import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Header
    nav: {
      collection: "COLLECTION",
      source: "THE SOURCE",
      experience: "EXPERIENCE"
    },
    
    // Hero
    hero: {
      badge: "MADE IN SPAIN",
      title1: "VOLCANO",
      title2: "VODKA",
      subtitle: "Born in Spain. Forged in Stone. Captured in Ice.",
      subtitleLine2: "A luxury spirit born from the earth's most powerful elements.",
      stat1Value: "40%",
      stat1Label: "ABV",
      stat2Value: "OBSIDIAN",
      stat2Label: "FILTERED",
      stat3Value: "SPANISH",
      stat3Label: "GRAPES",
      ctaPrimary: "EXPLORE COLLECTION",
      ctaSecondary: "OUR STORY",
      scroll: "SCROLL",
      limitedEdition: "LIMITED EDITION"
    },
    
    // Product Showcase
    products: {
      sectionLabel: "COLLECTION — 01",
      sectionTitle1: "THE",
      sectionTitle2: "COLLECTION",
      reserve: "RESERVE",
      items: [
        {
          name: "THE SPARK",
          subtitle: "30ML",
          description: "Travel and discovery starter. The perfect introduction to the Volcano experience.",
          price: "€29",
          limited: "DISCOVERY SIZE",
          features: ["40% ABV", "30ML", "OBSIDIAN FILTERED"]
        },
        {
          name: "THE CORE",
          subtitle: "700ML",
          description: "The standard format for the full Volcano experience. Pure volcanic spirit.",
          price: "€80",
          limited: "SIGNATURE FORMAT",
          features: ["40% ABV", "700ML", "OBSIDIAN FILTERED"]
        },
        {
          name: "THE ERUPTION",
          subtitle: "1.75L",
          description: "For collectors and group rituals. A commanding presence for those who share the experience.",
          price: "€179",
          limited: "COLLECTOR'S CHOICE",
          features: ["40% ABV", "1.75L", "OBSIDIAN FILTERED"]
        },
        {
          name: "THE SUMMIT",
          subtitle: "5L",
          description: "A statement piece for true devotees. The pinnacle of the Volcano collection.",
          price: "€449",
          limited: "LIMITED EDITION",
          features: ["40% ABV", "5L", "OBSIDIAN FILTERED"]
        }
      ]
    },
    
    // Philosophy
    philosophy: {
      sectionLabel: "THE SOURCE — 02",
      sectionTitle1: "THE",
      sectionTitle2: "JOURNEY",
      sectionSubtitle: "From Spanish vineyards to volcanic depths, discover the four phases that create liquid perfection.",
      phases: [
        {
          number: "01",
          title: "THE HARVEST",
          description: "Premium Spanish grapes selected at peak ripeness. Only the finest vineyards contribute to Volcano Vodka."
        },
        {
          number: "02",
          title: "THE DISTILLATION",
          description: "Distilled to perfection at 40% ABV. Precision craft that honors tradition while embracing innovation."
        },
        {
          number: "03",
          title: "THE FILTRATION",
          description: "Volcanic mineral filtration through basalt and obsidian. The fire-born stone purifies and refines."
        },
        {
          number: "04",
          title: "THE VESSEL",
          description: "Captured in crystal. Each bottle is a masterpiece designed to preserve and present volcanic perfection."
        }
      ],
      manifestoTitle: "THE MANIFESTO",
      manifesto: [
        "The Earth's inner fire meets Spain's finest grapes.",
        "What rises from the volcano rises above all else.",
        "Born in Spain. Forged in Stone. Captured in Ice.",
        "Experience the Explosive."
      ],
      quote: '"What rises from the volcano rises above all else."',
      quoteAuthor: "— Volcano Vodka",
      stats: {
        origin: { value: "SPAIN", label: "ORIGIN" },
        abv: { value: "40%", label: "ABV" },
        filtered: { value: "OBSIDIAN", label: "FILTERED" },
        grapes: { value: "GRAPES", label: "SPANISH" }
      }
    },
    
    // Community
    community: {
      sectionLabel: "THE PRESENTATION — 03",
      sectionTitle1: "UNBOX",
      sectionTitle2: "THE EXPERIENCE",
      sectionDescription: "Volcano Vodka isn't just a drink—it's a ritual. Every detail of the packaging is crafted to elevate the moment.",
      packagingTitle: "THE COMPLETE SET",
      packagingDescription: "Each Volcano Vodka comes encased in a premium presentation that matches the quality within. The experience begins before the first pour.",
      availableIn: "AVAILABLE IN:",
      colors: [
        { name: "MIDNIGHT BLACK" },
        { name: "ARCTIC WHITE" },
        { name: "VOLCANIC RED" }
      ],
      features: [
        { title: "LUXURY BOX", description: "Each bottle arrives in a custom matte black presentation box." },
        { title: "LED GLOW BASE", description: "Illuminated pedestal transforms the bottle into a centerpiece." },
        { title: "MYSTERY ITEM", description: "Every box includes a surprise collector's piece." }
      ],
      joinTitle: "JOIN THE INNER CIRCLE",
      joinDescription: "Be the first to know about new releases, exclusive colorways, and limited editions.",
      emailPlaceholder: "ENTER YOUR EMAIL",
      joinButton: "JOIN",
      disclaimer: "Must be 18+ to subscribe. We respect your privacy.",
      featureItems: [
        { number: "01", title: "MADE IN SPAIN", description: "Crafted with premium Spanish grapes" },
        { number: "02", title: "VOLCANIC FILTERED", description: "Purified through basalt and obsidian" },
        { number: "03", title: "LIMITED EDITIONS", description: "Exclusive colorways and collector's sets" }
      ]
    },
    
    // Footer
    footer: {
      tagline: "Born in Spain. Forged in Stone. Captured in Ice.",
      madeIn: "MADE IN SPAIN",
      obsidianFiltered: "— OBSIDIAN FILTERED",
      philosophy: ["SPANISH GRAPES.", "VOLCANIC STONE.", "CRYSTAL ICE."],
      copyright: "VOLCANO VODKA. ALL RIGHTS RESERVED.",
      ageWarning: "MUST BE 18+ TO PURCHASE",
      drinkResponsibly: "PLEASE DRINK RESPONSIBLY"
    }
  },
  
  es: {
    // Header
    nav: {
      collection: "COLECCIÓN",
      source: "EL ORIGEN",
      experience: "EXPERIENCIA"
    },
    
    // Hero
    hero: {
      badge: "HECHO EN ESPAÑA",
      title1: "VOLCANO",
      title2: "VODKA",
      subtitle: "Nacido en España. Forjado en Piedra. Capturado en Hielo.",
      subtitleLine2: "Un espíritu de lujo nacido de los elementos más poderosos de la tierra.",
      stat1Value: "40%",
      stat1Label: "ABV",
      stat2Value: "OBSIDIANA",
      stat2Label: "FILTRADO",
      stat3Value: "UVAS",
      stat3Label: "ESPAÑOLAS",
      ctaPrimary: "EXPLORAR COLECCIÓN",
      ctaSecondary: "NUESTRA HISTORIA",
      scroll: "DESLIZA",
      limitedEdition: "EDICIÓN LIMITADA"
    },
    
    // Product Showcase
    products: {
      sectionLabel: "COLECCIÓN — 01",
      sectionTitle1: "LA",
      sectionTitle2: "COLECCIÓN",
      reserve: "RESERVAR",
      items: [
        {
          name: "THE SPARK",
          subtitle: "30ML",
          description: "Inicio de viaje y descubrimiento. La introducción perfecta a la experiencia Volcano.",
          price: "€29",
          limited: "TAMAÑO DESCUBRIMIENTO",
          features: ["40% ABV", "30ML", "FILTRADO OBSIDIANA"]
        },
        {
          name: "THE CORE",
          subtitle: "700ML",
          description: "El formato estándar para la experiencia Volcano completa. Espíritu volcánico puro.",
          price: "€80",
          limited: "FORMATO SIGNATURE",
          features: ["40% ABV", "700ML", "FILTRADO OBSIDIANA"]
        },
        {
          name: "THE ERUPTION",
          subtitle: "1.75L",
          description: "Para coleccionistas y rituales grupales. Una presencia imponente para quienes comparten la experiencia.",
          price: "€179",
          limited: "ELECCIÓN COLECCIONISTA",
          features: ["40% ABV", "1.75L", "FILTRADO OBSIDIANA"]
        },
        {
          name: "THE SUMMIT",
          subtitle: "5L",
          description: "Una pieza de declaración para los verdaderos devotos. El pináculo de la colección Volcano.",
          price: "€449",
          limited: "EDICIÓN LIMITADA",
          features: ["40% ABV", "5L", "FILTRADO OBSIDIANA"]
        }
      ]
    },
    
    // Philosophy
    philosophy: {
      sectionLabel: "EL ORIGEN — 02",
      sectionTitle1: "EL",
      sectionTitle2: "VIAJE",
      sectionSubtitle: "Desde los viñedos españoles hasta las profundidades volcánicas, descubre las cuatro fases que crean la perfección líquida.",
      phases: [
        {
          number: "01",
          title: "LA COSECHA",
          description: "Uvas españolas premium seleccionadas en su punto óptimo de madurez. Solo los mejores viñedos contribuyen a Volcano Vodka."
        },
        {
          number: "02",
          title: "LA DESTILACIÓN",
          description: "Destilado a la perfección al 40% ABV. Artesanía de precisión que honra la tradición mientras abraza la innovación."
        },
        {
          number: "03",
          title: "LA FILTRACIÓN",
          description: "Filtración mineral volcánica a través de basalto y obsidiana. La piedra nacida del fuego purifica y refina."
        },
        {
          number: "04",
          title: "EL RECIPIENTE",
          description: "Capturado en cristal. Cada botella es una obra maestra diseñada para preservar y presentar la perfección volcánica."
        }
      ],
      manifestoTitle: "EL MANIFIESTO",
      manifesto: [
        "El fuego interior de la Tierra se encuentra con las mejores uvas de España.",
        "Lo que surge del volcán se eleva por encima de todo lo demás.",
        "Nacido en España. Forjado en Piedra. Capturado en Hielo.",
        "Experimenta lo Explosivo."
      ],
      quote: '"Lo que surge del volcán se eleva por encima de todo lo demás."',
      quoteAuthor: "— Volcano Vodka",
      stats: {
        origin: { value: "ESPAÑA", label: "ORIGEN" },
        abv: { value: "40%", label: "ABV" },
        filtered: { value: "OBSIDIANA", label: "FILTRADO" },
        grapes: { value: "UVAS", label: "ESPAÑOLAS" }
      }
    },
    
    // Community
    community: {
      sectionLabel: "LA PRESENTACIÓN — 03",
      sectionTitle1: "DESEMPACA",
      sectionTitle2: "LA EXPERIENCIA",
      sectionDescription: "Volcano Vodka no es solo una bebida—es un ritual. Cada detalle del empaque está diseñado para elevar el momento.",
      packagingTitle: "EL SET COMPLETO",
      packagingDescription: "Cada Volcano Vodka viene en una presentación premium que iguala la calidad interior. La experiencia comienza antes del primer trago.",
      availableIn: "DISPONIBLE EN:",
      colors: [
        { name: "NEGRO MEDIANOCHE" },
        { name: "BLANCO ÁRTICO" },
        { name: "ROJO VOLCÁNICO" }
      ],
      features: [
        { title: "CAJA DE LUJO", description: "Cada botella llega en una caja de presentación negra mate personalizada." },
        { title: "BASE LED", description: "Pedestal iluminado que transforma la botella en una pieza central." },
        { title: "ARTÍCULO MISTERIO", description: "Cada caja incluye una pieza de colección sorpresa." }
      ],
      joinTitle: "ÚNETE AL CÍRCULO INTERNO",
      joinDescription: "Sé el primero en conocer nuevos lanzamientos, colores exclusivos y ediciones limitadas.",
      emailPlaceholder: "INGRESA TU EMAIL",
      joinButton: "UNIRSE",
      disclaimer: "Debes ser mayor de 18 años para suscribirte. Respetamos tu privacidad.",
      featureItems: [
        { number: "01", title: "HECHO EN ESPAÑA", description: "Elaborado con uvas españolas premium" },
        { number: "02", title: "FILTRADO VOLCÁNICO", description: "Purificado a través de basalto y obsidiana" },
        { number: "03", title: "EDICIONES LIMITADAS", description: "Colores exclusivos y sets de colección" }
      ]
    },
    
    // Footer
    footer: {
      tagline: "Nacido en España. Forjado en Piedra. Capturado en Hielo.",
      madeIn: "HECHO EN ESPAÑA",
      obsidianFiltered: "— FILTRADO OBSIDIANA",
      philosophy: ["UVAS ESPAÑOLAS.", "PIEDRA VOLCÁNICA.", "HIELO CRISTALINO."],
      copyright: "VOLCANO VODKA. TODOS LOS DERECHOS RESERVADOS.",
      ageWarning: "DEBES SER MAYOR DE 18 AÑOS PARA COMPRAR",
      drinkResponsibly: "POR FAVOR BEBE RESPONSABLEMENTE"
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  
  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };
  
  const t = translations[language];
  
  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
